// Difficulty Level Management System

export const DIFFICULTY_LEVELS = {
  EASY: 'easy',
  MEDIUM: 'medium',
  HARD: 'hard'
};

export const DIFFICULTY_CONFIG = {
  easy: {
    id: 'easy',
    name: 'Easy',
    label: 'Ages 4-6',
    description: 'Simple questions for young learners',
    icon: '🌱',
    color: '#2ecc71',
    questionsPerQuiz: 8,
    timePerQuestion: null, // No time limit
    passingScore: 60
  },
  medium: {
    id: 'medium',
    name: 'Medium',
    label: 'Ages 7-9',
    description: 'Moderate challenge for growing minds',
    icon: '🌟',
    color: '#f39c12',
    questionsPerQuiz: 10,
    timePerQuestion: null,
    passingScore: 70
  },
  hard: {
    id: 'hard',
    name: 'Hard',
    label: 'Ages 10+',
    description: 'Advanced questions for experts',
    icon: '🔥',
    color: '#e74c3c',
    questionsPerQuiz: 12,
    timePerQuestion: null,
    passingScore: 80
  }
};

/**
 * Get difficulty configuration
 */
export const getDifficultyConfig = (level) => {
  return DIFFICULTY_CONFIG[level] || DIFFICULTY_CONFIG.medium;
};

/**
 * Get all difficulty levels
 */
export const getAllDifficulties = () => {
  return Object.values(DIFFICULTY_CONFIG);
};

/**
 * Suggest difficulty based on user's age
 */
export const suggestDifficultyByAge = (age) => {
  if (age <= 6) return DIFFICULTY_LEVELS.EASY;
  if (age <= 9) return DIFFICULTY_LEVELS.MEDIUM;
  return DIFFICULTY_LEVELS.HARD;
};

/**
 * Suggest difficulty based on performance
 * @param {Object} userProgress - User's progress data
 * @param {String} currentDifficulty - Current difficulty level
 * @returns {String} Suggested difficulty level
 */
export const suggestDifficultyByPerformance = (userProgress, currentDifficulty) => {
  if (!userProgress || Object.keys(userProgress).length === 0) {
    return currentDifficulty;
  }

  // Calculate average score across all categories
  const categories = Object.values(userProgress);
  const totalQuizzes = categories.reduce((sum, cat) => sum + (cat.totalQuizzes || 0), 0);
  const totalScore = categories.reduce((sum, cat) => sum + (cat.totalScore || 0), 0);
  const totalQuestions = categories.reduce((sum, cat) => sum + ((cat.totalQuizzes || 0) * 10), 0);

  if (totalQuizzes < 5) {
    // Not enough data, keep current difficulty
    return currentDifficulty;
  }

  const averagePercentage = totalQuestions > 0 ? (totalScore / totalQuestions) * 100 : 0;

  // Suggest based on performance
  if (currentDifficulty === DIFFICULTY_LEVELS.EASY) {
    if (averagePercentage >= 85 && totalQuizzes >= 10) {
      return DIFFICULTY_LEVELS.MEDIUM; // Suggest upgrade
    }
  } else if (currentDifficulty === DIFFICULTY_LEVELS.MEDIUM) {
    if (averagePercentage >= 90 && totalQuizzes >= 15) {
      return DIFFICULTY_LEVELS.HARD; // Suggest upgrade
    } else if (averagePercentage < 60 && totalQuizzes >= 10) {
      return DIFFICULTY_LEVELS.EASY; // Suggest downgrade
    }
  } else if (currentDifficulty === DIFFICULTY_LEVELS.HARD) {
    if (averagePercentage < 65 && totalQuizzes >= 10) {
      return DIFFICULTY_LEVELS.MEDIUM; // Suggest downgrade
    }
  }

  return currentDifficulty; // No change
};

/**
 * Get performance message based on score and difficulty
 */
export const getPerformanceMessage = (score, totalQuestions, difficulty) => {
  const percentage = (score / totalQuestions) * 100;
  const config = getDifficultyConfig(difficulty);

  if (percentage === 100) {
    return {
      title: 'Perfect! 🎉',
      message: 'You got every question right!',
      emoji: '🏆'
    };
  } else if (percentage >= config.passingScore + 20) {
    return {
      title: 'Excellent! ⭐',
      message: 'You did amazing!',
      emoji: '🌟'
    };
  } else if (percentage >= config.passingScore) {
    return {
      title: 'Great Job! 👍',
      message: 'You passed with flying colors!',
      emoji: '✨'
    };
  } else if (percentage >= config.passingScore - 10) {
    return {
      title: 'Good Try! 💪',
      message: 'Almost there! Keep practicing!',
      emoji: '📚'
    };
  } else {
    return {
      title: 'Keep Learning! 🌱',
      message: 'Practice makes perfect!',
      emoji: '💡'
    };
  }
};

/**
 * Filter questions by difficulty
 */
export const filterQuestionsByDifficulty = (questions, difficulty) => {
  if (!questions || questions.length === 0) return [];
  
  return questions.filter(q => {
    if (!q.difficulty) return true; // Include questions without difficulty
    return q.difficulty === difficulty;
  });
};

/**
 * Auto-assign difficulty to questions based on index
 * First 4 questions: Easy
 * Next 4 questions: Medium  
 * Last 4 questions: Hard
 */
export const assignDifficultyToQuestions = (questions) => {
  if (!questions || questions.length === 0) return [];
  
  return questions.map((q, index) => {
    if (q.difficulty) return q; // Keep existing difficulty
    
    // Auto-assign based on position
    if (index < 4) {
      return { ...q, difficulty: DIFFICULTY_LEVELS.EASY };
    } else if (index < 8) {
      return { ...q, difficulty: DIFFICULTY_LEVELS.MEDIUM };
    } else {
      return { ...q, difficulty: DIFFICULTY_LEVELS.HARD };
    }
  });
};

/**
 * Get questions for quiz based on difficulty
 */
export const getQuestionsForDifficulty = (allQuestions, difficulty) => {
  const config = getDifficultyConfig(difficulty);
  
  // Auto-assign difficulty if not present
  const questionsWithDifficulty = assignDifficultyToQuestions(allQuestions);
  
  // Filter by difficulty
  const filtered = questionsWithDifficulty.filter(q => q.difficulty === difficulty);
  
  // If not enough questions, add some from adjacent difficulty
  let questionsToUse = [...filtered];
  if (questionsToUse.length < config.questionsPerQuiz) {
    const remaining = questionsWithDifficulty.filter(q => q.difficulty !== difficulty);
    questionsToUse = [...questionsToUse, ...remaining];
  }
  
  // Shuffle and return required number
  const shuffled = [...questionsToUse].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, config.questionsPerQuiz);
};

/**
 * Save difficulty preference
 */
export const saveDifficultyPreference = (profileId, difficulty) => {
  try {
    const key = `difficulty_${profileId}`;
    localStorage.setItem(key, difficulty);
  } catch (error) {
    console.error('Error saving difficulty preference:', error);
  }
};

/**
 * Load difficulty preference
 */
export const loadDifficultyPreference = (profileId) => {
  try {
    const key = `difficulty_${profileId}`;
    return localStorage.getItem(key) || DIFFICULTY_LEVELS.MEDIUM;
  } catch (error) {
    console.error('Error loading difficulty preference:', error);
    return DIFFICULTY_LEVELS.MEDIUM;
  }
};

/**
 * Get difficulty badge color
 */
export const getDifficultyBadgeColor = (difficulty) => {
  const config = getDifficultyConfig(difficulty);
  return config.color;
};

/**
 * Get difficulty display name
 */
export const getDifficultyDisplayName = (difficulty) => {
  const config = getDifficultyConfig(difficulty);
  return `${config.icon} ${config.name} (${config.label})`;
};
